import _ from 'lodash';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

type RelativeRoutingType = 'route' | 'path';

export type ParamKeyValuePair = [string, string];

export type URLSearchParamsInit = string | ParamKeyValuePair[] | Record<string, string | string[]> | URLSearchParams;

export interface NavigateOptions {
    replace?: boolean;
    state?: any;
    preventScrollReset?: boolean;
    relative?: RelativeRoutingType;
}

export type SetURLSearchParams = (
    nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
    navigateOpts?: NavigateOptions
) => void;

/**
 * Creates a URLSearchParams object using the given initializer.
 *
 * This is identical to `new URLSearchParams(init)` except it also
 * supports arrays as values in the object form of the initializer
 * instead of just strings. This is convenient when you need multiple
 * values for a given key, but don't want to use an array initializer.
 *
 * For example, instead of:
 *
 *   let searchParams = new URLSearchParams([
 *     ['sort', 'name'],
 *     ['sort', 'price']
 *   ]);
 *
 * you can do:
 *
 *   let searchParams = createSearchParams({
 *     sort: ['name', 'price']
 *   });
 */
export function createSearchParams(init: URLSearchParamsInit = ''): URLSearchParams {
    return new URLSearchParams(
        typeof init === 'string' || Array.isArray(init) || init instanceof URLSearchParams
            ? init
            : Object.keys(init).reduce<ParamKeyValuePair[]>((memo, key) => {
                  const value = init[key];
                  return memo.concat(Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]]);
              }, [])
    );
}

export function getSearchParamsForLocation(
    locationSearch: string,
    defaultSearchParams: URLSearchParams | null
): URLSearchParams {
    const searchParams = createSearchParams(locationSearch);

    if (defaultSearchParams) {
        for (const key of _.keys(defaultSearchParams)) {
            if (!searchParams.has(key)) {
                defaultSearchParams.getAll(key).forEach((value) => {
                    searchParams.append(key, value);
                });
            }
        }
    }

    return searchParams;
}

/**
 * A convenient wrapper for reading and writing search parameters via the
 * URLSearchParams interface.
 */
export function useSearchParams(defaultInit?: URLSearchParamsInit): [URLSearchParams, SetURLSearchParams] {
    const defaultSearchParamsRef = React.useRef(createSearchParams(defaultInit));
    const hasSetSearchParamsRef = React.useRef(false);

    const location = useLocation();
    const searchParams = React.useMemo(
        () =>
            // Only merge in the defaults if we haven't yet called setSearchParams.
            // Once we call that we want those to take precedence, otherwise you can't
            // remove a param with setSearchParams({}) if it has an initial value
            getSearchParamsForLocation(
                location.search,
                hasSetSearchParamsRef.current ? null : defaultSearchParamsRef.current
            ),
        [location.search]
    );

    const history = useHistory();
    const setSearchParams = React.useCallback<SetURLSearchParams>(
        (nextInit, navigateOptions) => {
            const newSearchParams = createSearchParams(
                typeof nextInit === 'function' ? nextInit(searchParams) : nextInit
            );
            hasSetSearchParamsRef.current = true;
            history.replace(`?${newSearchParams}`, navigateOptions);
        },
        [history, searchParams]
    );

    return [searchParams, setSearchParams];
}
